/**
 * LoginModal - Unified login modal for wallet and email authentication.
 * 
 * Provides two login options:
 * 1. Connect Wallet - External Solana wallets (Phantom, Solflare, Coinbase, Trust, WalletConnect)
 * 2. Continue with Email - Social/email login with embedded wallet (via Privy)
 * 
 * Both flows stay within the same modal for consistent UX.
 * 
 * Mobile-optimized:
 * - Shows Nordex app download card on mobile
 * - Features WalletConnect prominently
 * - Deep links to wallet apps with store fallback
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { WalletReadyState } from "@solana/wallet-adapter-base";
import { usePrivyAuth } from "@/backend/use-privy-auth";
import { useAuth } from "@/backend/use-auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  WalletIcon,
  ArrowLeftIcon,
  Loader2Icon,
  AlertCircleIcon,
  CheckIcon,
  Mail,
  ExternalLink,
  Star,
} from "lucide-react";

// Apple logo SVG icon component
const AppleIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 384 512"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

// Google Play logo SVG icon component
const PlayStoreIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 512 512"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
  </svg>
);

// Site's green color for glow effect
const SITE_GREEN = "#2ebd85";
const SITE_GREEN_LIGHT = "#5fd4a4";

// Inject keyframes for the animated glow border
const injectGlowStyles = () => {
  const styleId = "nordex-app-glow-animation";
  if (document.getElementById(styleId)) return;
  
  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = `
    @keyframes nordex-glow-spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes nordex-glow-pulse {
      0%, 100% { opacity: 0.4; }
      50% { opacity: 0.7; }
    }
  `;
  document.head.appendChild(style);
};
import {
  getPlatform,
  isNordexAppAvailable,
  openNordexApp,
  openWalletApp,
  openWalletStore,
  type WalletKey,
} from "@/lib/mobile-wallet-utils";

interface LoginModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ModalView = "options" | "wallet";

export function LoginModal({ open, onOpenChange }: LoginModalProps) {
  const [view, setView] = useState<ModalView>("options");
  const [connectingWallet, setConnectingWallet] = useState<string | null>(null);
  const [walletError, setWalletError] = useState<string | null>(null);
  // Track wallets that failed deep link (not installed) - show "Get" button
  const [walletsNotInstalled, setWalletsNotInstalled] = useState<Set<string>>(new Set());
  
  const { wallets, select, connected, wallet } = useWallet();
  const { login: privyLogin } = usePrivyAuth();
  const { isAuthenticated: walletAuthenticated, resetAuthAttempt, authenticate, markUserInitiated } = useAuth();
  
  // Platform detection for mobile-specific UI
  const platform = useMemo(() => getPlatform(), []);
  const isMobile = platform === "ios" || platform === "android";
  const showAppDownload = isMobile && isNordexAppAvailable(platform);

  // Inject glow animation styles
  useEffect(() => {
    injectGlowStyles();
  }, []);

  // Reset auth attempt when modal opens - allows re-triggering sign message
  // Also mark as user-initiated so the auth provider knows this is an explicit action
  useEffect(() => {
    if (open) {
      resetAuthAttempt();
      markUserInitiated();
    }
  }, [open, resetAuthAttempt, markUserInitiated]);

  // Reset state when modal opens/closes
  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      // Reset to initial state when closing
      setTimeout(() => {
        setView("options");
        setConnectingWallet(null);
        setWalletError(null);
      }, 200);
    }
    onOpenChange(isOpen);
  }, [onOpenChange]);

  // Close modal when user becomes authenticated via wallet
  // NOTE: Don't check privyAuthenticated here!
  // - Privy login: We close our modal BEFORE opening Privy's UI (in handlePrivyLogin)
  // - Privy logout: Privy SDK's authenticated state can be slow to update,
  //   which would immediately close the modal before user sees it
  // IMPORTANT: Also check `connected` to prevent stale tokens (e.g. from a
  // previous Privy session stored in localStorage) from immediately closing
  // the modal before a wallet is actually connected.
  useEffect(() => {
    if (walletAuthenticated && open && connected) {
      handleOpenChange(false);
    }
  }, [walletAuthenticated, open, handleOpenChange, connected]);

  // Handle wallet selection and connection
  const handleWalletSelect = useCallback(async (walletName: string) => {
    try {
      setWalletError(null);
      
      // Find the wallet in the wallets array
      const walletToSelect = wallets.find(w => w.adapter.name === walletName);
      if (!walletToSelect) {
        throw new Error("Wallet not found");
      }
      
      // If this wallet is already connected but not authenticated, trigger auth directly
      // This handles the case where user cancelled sign message and wants to try again
      // Don't show spinner - the wallet popup is the visual feedback
      if (connected && wallet?.adapter.name === walletName && !walletAuthenticated) {
        try {
          await authenticate();
          // Auth succeeded - modal will close via the walletAuthenticated check
        } catch (error) {
          // User rejected or auth failed - that's okay, they can try again
          const errorMessage = error instanceof Error ? error.message : String(error);
          if (!errorMessage.includes("User rejected") && !errorMessage.includes("rejected")) {
            setWalletError("Authentication failed. Please try again.");
          }
        }
        return;
      }
      
      // On mobile, use deep linking for Phantom and Solflare
      const walletNameLower = walletName.toLowerCase();
      if (isMobile && (walletNameLower === "phantom" || walletNameLower === "solflare")) {
        const walletKey = walletNameLower as WalletKey;
        
        // If already known to not be installed, go directly to store
        if (walletsNotInstalled.has(walletNameLower)) {
          openWalletStore(walletKey);
          return;
        }
        
        setConnectingWallet(walletName);
        
        // Try to open the wallet app via deep link
        const result = await openWalletApp(walletKey);
        
        if (result === "not_installed") {
          // Wallet not installed - mark it and show Get button
          setWalletsNotInstalled(prev => new Set(prev).add(walletNameLower));
          setConnectingWallet(null);
          // Don't show error, the UI will update to show "Get" button
        }
        // If result is "opened", the user left the page to the wallet app
        // They'll come back and connect via the wallet adapter
        return;
      }
      
      // New wallet selection - show loading state
      setConnectingWallet(walletName);
      
      // Mark as user-initiated so auth provider knows to prompt for signing
      markUserInitiated();
      resetAuthAttempt();
      
      // Select the wallet (will trigger connect flow)
      // autoConnect={true} in WalletProvider will handle the connection automatically
      select(walletToSelect.adapter.name);
      
    } catch (error: unknown) {
      console.error("Failed to select wallet:", error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (!errorMessage.includes("User rejected")) {
        setWalletError("Failed to connect. Please try again.");
      }
      setConnectingWallet(null);
    }
  }, [select, wallets, connected, wallet, walletAuthenticated, authenticate, isMobile, walletsNotInstalled, markUserInitiated, resetAuthAttempt]);
  
  // Handle "Get" button click for mobile wallets
  const handleGetWallet = useCallback((walletName: string) => {
    const walletNameLower = walletName.toLowerCase();
    if (walletNameLower === "phantom" || walletNameLower === "solflare") {
      openWalletStore(walletNameLower as WalletKey);
    }
  }, []);

  // Clear connecting state if connection succeeds or modal closes
  useEffect(() => {
    if (connected && connectingWallet) {
      // Connection succeeded - modal will close
      setConnectingWallet(null);
    }
  }, [connected, connectingWallet]);

  // Handle Privy login - triggers Privy modal and closes this one
  const handlePrivyLogin = useCallback(() => {
    privyLogin();
    handleOpenChange(false);
  }, [privyLogin, handleOpenChange]);

  // Check if a wallet is actually installed (browser extension detected)
  // Note: Loadable means it CAN be loaded (e.g. web wallet), not that it's installed
  const isWalletInstalled = (readyState: WalletReadyState) => {
    return readyState === WalletReadyState.Installed;
  };

  // Get wallet brand colors for icon backgrounds
  const getWalletBgColor = (walletName: string) => {
    switch (walletName.toLowerCase()) {
      case "phantom":
        return "bg-[#AB9FF2]/15";
      case "solflare":
        return "bg-[#FC7227]/15";
      case "coinbase wallet":
        return "bg-[#0052FF]/15";
      case "trust":
        return "bg-[#3375BB]/15";
      case "walletconnect":
        return "bg-[#3B99FC]/15";
      default:
        return "bg-primary/10";
    }
  };

  // Get display name for wallets (some adapters have short names)
  const getDisplayName = (walletName: string) => {
    switch (walletName.toLowerCase()) {
      case "trust":
        return "Trust Wallet";
      default:
        return walletName;
    }
  };

  // Get icon size class - some wallet icons need larger display
  const getIconSizeClass = (walletName: string) => {
    switch (walletName.toLowerCase()) {
      case "trust":
        return "h-10 w-10"; // Larger to compensate for icon whitespace
      default:
        return "h-7 w-7";
    }
  };

  // Sort wallets: 
  // Desktop: installed wallets first, then alphabetically
  // Mobile: WalletConnect first (featured), then Phantom/Solflare, hide Coinbase/Trust
  const sortedWallets = useMemo(() => {
    return [...wallets]
      // Filter wallets based on platform
      .filter(w => {
        const name = w.adapter.name.toLowerCase();
        if (!isMobile) return true; // Show all on desktop
        
        // On mobile, hide Trust and Coinbase (poor mobile support)
        if (name === "trust" || name === "coinbase wallet") return false;
        
        return true;
      })
      .sort((a, b) => {
        const aName = a.adapter.name.toLowerCase();
        const bName = b.adapter.name.toLowerCase();
        
        if (isMobile) {
          // On mobile: WalletConnect first (featured)
          if (aName === "walletconnect") return -1;
          if (bName === "walletconnect") return 1;
          
          // Then Phantom and Solflare
          const mobileOrder = ["phantom", "solflare"];
          const aIdx = mobileOrder.indexOf(aName);
          const bIdx = mobileOrder.indexOf(bName);
          if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
          if (aIdx !== -1) return -1;
          if (bIdx !== -1) return 1;
        }
        
        // Desktop: installed wallets first
        const aInstalled = isWalletInstalled(a.adapter.readyState);
        const bInstalled = isWalletInstalled(b.adapter.readyState);
        if (aInstalled && !bInstalled) return -1;
        if (!aInstalled && bInstalled) return 1;
        
        // Alphabetically within same group
        return a.adapter.name.localeCompare(b.adapter.name);
      });
  }, [wallets, isMobile]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        {/* Options View */}
        {view === "options" && (
          <>
            <DialogHeader className="text-center sm:text-center pb-1">
              <DialogTitle className="text-xl font-bold">
                Connect Wallet/Login
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-3 pt-2">
              {/* Wallet Option - Orange for crypto/wallet association */}
              <button
                onClick={() => setView("wallet")}
                className={cn(
                  "group relative flex items-center gap-4 rounded-xl border border-border bg-card p-4",
                  "transition-all duration-200 hover:border-primary/50 hover:bg-accent/50",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                )}
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-orange-500/10 text-orange-500 transition-colors group-hover:bg-orange-500/20">
                  <WalletIcon className="h-5 w-5" />
                </div>
                <div className="flex flex-col items-start text-left">
                  <span className="font-semibold">Connect Wallet</span>
                  <span className="text-sm text-muted-foreground">
                    Phantom, Solflare, and more
                  </span>
                </div>
              </button>

              {/* Email Option - Blue for email association */}
              <button
                onClick={handlePrivyLogin}
                className={cn(
                  "group relative flex items-center gap-4 rounded-xl border border-border bg-card p-4",
                  "transition-all duration-200 hover:border-primary/50 hover:bg-accent/50",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                )}
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-500/10 text-blue-500 transition-colors group-hover:bg-blue-500/20">
                  <Mail className="h-5 w-5" />
                </div>
                <div className="flex flex-col items-start text-left">
                  <span className="font-semibold">Continue with Email / Social</span>
                  <span className="text-sm text-muted-foreground">
                    Email, Google & more via Privy
                  </span>
                </div>
              </button>
            </div>

            <div className="mt-4 text-center text-xs text-muted-foreground space-y-1">
              <p>
                By continuing, you agree to our Terms of Use and Privacy Policy
              </p>
              <p>
                Charts are powered by{' '}
                <a 
                  href="https://www.tradingview.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  TradingView
                </a>
              </p>
            </div>
          </>
        )}

        {/* Wallet Selection View */}
        {view === "wallet" && (
          <>
            <DialogHeader>
              <button
                onClick={() => {
                  setView("options");
                  setConnectingWallet(null);
                  setWalletError(null);
                }}
                className="absolute left-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                <span className="sr-only">Back</span>
              </button>
              <DialogTitle className="text-center text-xl font-bold">
                Connect Wallet
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-3 pt-4">
              {/* Mobile App Download Card - only on mobile, RECOMMENDED with green glow */}
              {showAppDownload && (
                <button
                  onClick={() => openNordexApp()}
                  className={cn(
                    "group relative flex items-center gap-3 rounded-xl p-3 overflow-visible",
                    "transition-all duration-300 hover:scale-[1.02]",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  )}
                >
                  {/* Animated rotating gradient border - the traveling green light */}
                  <span className="absolute inset-0 rounded-xl overflow-hidden">
                    <span 
                      className="absolute inset-[-150%]"
                      style={{
                        background: `conic-gradient(from 0deg, transparent 60%, ${SITE_GREEN}99 75%, ${SITE_GREEN_LIGHT}E6 85%, ${SITE_GREEN}99 95%, transparent 100%)`,
                        animation: 'nordex-glow-spin 3s linear infinite',
                        opacity: 0.7,
                      }}
                    />
                  </span>
                  
                  {/* Subtle pulsing border */}
                  <span 
                    className="absolute inset-0 rounded-xl pointer-events-none"
                    style={{ 
                      border: `1px solid ${SITE_GREEN}4D`,
                      animation: 'nordex-glow-pulse 2s ease-in-out infinite' 
                    }}
                  />
                  
                  {/* Card background */}
                  <span className="absolute inset-[1px] rounded-[10px] bg-card" />

                  {/* Recommended badge - moved up */}
                  <div className="absolute -top-3 left-4 flex items-center gap-1 bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5 rounded-full z-10">
                    <Star className="h-2.5 w-2.5 fill-current" />
                    Recommended
                  </div>

                  {/* App icon using SVG for crisp rendering at any resolution */}
                  <div className="relative h-11 w-11 rounded-xl shrink-0 bg-[#0a0a0a] flex items-center justify-center overflow-hidden p-2">
                    <img 
                      src="/nordex_icon_white.svg" 
                      alt="Nordex" 
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <div className="relative flex flex-col items-start text-left flex-1 min-w-0">
                    <span className="font-semibold text-sm">Get the Nordex App</span>
                    <span className="text-xs text-muted-foreground">
                      Available on {platform === "android" ? "Android" : "iOS"}
                    </span>
                  </div>
                  <div className="relative flex items-center gap-1.5 bg-black text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                    {platform === "android" ? (
                      <PlayStoreIcon className="h-3.5 w-3.5" />
                    ) : (
                      <AppleIcon className="h-3.5 w-3.5" />
                    )}
                    <span>Download</span>
                  </div>
                </button>
              )}

              {/* Divider on mobile */}
              {showAppDownload && (
                <div className="flex items-center gap-2 py-1">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground">or connect wallet</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
              )}

              {sortedWallets.map((wallet) => {
                const walletNameLower = wallet.adapter.name.toLowerCase();
                const isInstalled = isWalletInstalled(wallet.adapter.readyState);
                const isCurrentlyConnecting = connectingWallet === wallet.adapter.name;
                const isWalletConnect = walletNameLower === "walletconnect";
                const isMobileWallet = walletNameLower === "phantom" || walletNameLower === "solflare";
                const isNotInstalledMobile = isMobile && isMobileWallet && walletsNotInstalled.has(walletNameLower);
                
                return (
                  <button
                    key={wallet.adapter.name}
                    onClick={() => {
                      if (isNotInstalledMobile) {
                        handleGetWallet(wallet.adapter.name);
                      } else {
                        handleWalletSelect(wallet.adapter.name);
                      }
                    }}
                    disabled={!!connectingWallet && !isNotInstalledMobile}
                    className={cn(
                      "group relative flex items-center gap-4 rounded-xl border border-border bg-card p-4",
                      "transition-all duration-200 hover:border-primary/50 hover:bg-accent/50",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      "disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-card disabled:hover:border-border"
                    )}
                  >

                    {/* Wallet Icon */}
                    <div className={cn(
                      "flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-colors overflow-hidden",
                      getWalletBgColor(wallet.adapter.name)
                    )}>
                      {wallet.adapter.icon ? (
                        <img 
                          src={wallet.adapter.icon} 
                          alt={wallet.adapter.name}
                          className={cn("object-contain", getIconSizeClass(wallet.adapter.name))}
                        />
                      ) : (
                        <WalletIcon className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>
                    
                    {/* Wallet Info */}
                    <div className="flex flex-col items-start text-left flex-1 min-w-0">
                      <span className="font-semibold">{getDisplayName(wallet.adapter.name)}</span>
                      {/* Mobile-specific messaging */}
                      {isMobile && isWalletConnect ? (
                        <span className="text-sm text-muted-foreground">
                          Connect any mobile wallet
                        </span>
                      ) : isMobile && isMobileWallet ? (
                        <span className="text-sm text-muted-foreground">
                          {isNotInstalledMobile ? "Tap to install" : "Open in app"}
                        </span>
                      ) : isInstalled ? (
                        <span className="text-sm text-emerald-500 flex items-center gap-1">
                          <CheckIcon className="h-3 w-3" />
                          Installed
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          Not installed
                        </span>
                      )}
                    </div>

                    {/* Action button / Loading indicator */}
                    {isCurrentlyConnecting ? (
                      <Loader2Icon className="h-5 w-5 animate-spin text-primary flex-shrink-0" />
                    ) : isMobile && isMobileWallet ? (
                      <div className={cn(
                        "flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0",
                        isNotInstalledMobile 
                          ? "bg-primary text-primary-foreground" 
                          : "bg-primary/10 text-primary"
                      )}>
                        {isNotInstalledMobile ? (
                          <>
                            <ExternalLink className="h-3 w-3" />
                            <span>Get</span>
                          </>
                        ) : (
                          <span>Open</span>
                        )}
                      </div>
                    ) : null}
                  </button>
                );
              })}

              {/* Error message */}
              {walletError && (
                <p className="flex items-center justify-center gap-1 text-sm text-destructive">
                  <AlertCircleIcon className="h-4 w-4" />
                  {walletError}
                </p>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
